import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';

// ---------------------------------------------------------------------------
// Services nomenclature API — mirrors backend/src/routes/services.ts.
// The service catalogue is managed in Settings and attachable to projects
// (many), tasks (one), people (many) and money (invoices / SmartBill
// associations / bank reconciliation lines).
// ---------------------------------------------------------------------------

export type ServiceUnit = 'HOUR' | 'PROJECT' | 'MONTH' | 'PIECE';
export type ServiceCategory =
  | 'DEVELOPMENT'
  | 'DESIGN'
  | 'PM'
  | 'QA'
  | 'CONSULTING'
  | 'MAINTENANCE'
  | 'OTHER';

export interface Service {
  id: string;
  name: string;
  description: string | null;
  price: number | null;
  unit: ServiceUnit;
  category: ServiceCategory;
  isActive: boolean;
  departmentId: string | null;
  department: { id: string; name: string; color: string } | null;
  color: string | null;
  sortOrder: number;
  taskCount: number;
  projectCount: number;
  personCount: number;
  invoiceCount: number;
  createdAt: string;
  updatedAt: string;
}

/** Minimal service shape embedded on tasks/projects/people/invoices. */
export interface ServiceRef {
  id: string;
  name: string;
  color: string | null;
  category?: ServiceCategory;
}

export interface CreateServiceInput {
  name: string;
  description?: string | null;
  price?: number | null;
  unit?: ServiceUnit;
  category?: ServiceCategory;
  departmentId?: string | null;
  color?: string | null;
  sortOrder?: number;
  isActive?: boolean;
}

export type UpdateServiceInput = Partial<CreateServiceInput>;

export interface DeleteServiceResult {
  deleted: boolean;
  id: string;
  detachedTasks: number;
  detachedProjects: number;
  detachedPeople: number;
  detachedInvoices: number;
}

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** GET /api/services — the catalogue, ordered by sortOrder then name. */
export async function listServices(includeInactive = false): Promise<Service[]> {
  const qs = includeInactive ? '?includeInactive=true' : '';
  const res = await apiFetch<Envelope<Service[]>>(`/api/services${qs}`, {
    headers: authHeaders(),
  });
  return res.data;
}

/** POST /api/services — create a service (settings.canEditServices). */
export async function createService(input: CreateServiceInput): Promise<Service> {
  const res = await apiFetch<Envelope<Service>>('/api/services', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

/** PATCH /api/services/:id — partial update. */
export async function updateService(id: string, input: UpdateServiceInput): Promise<Service> {
  const res = await apiFetch<Envelope<Service>>(`/api/services/${id}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

/**
 * DELETE /api/services/:id — delete a service. Tags on tasks/projects/people/
 * invoices are detached; counts returned for the confirmation UI.
 */
export async function deleteService(id: string): Promise<DeleteServiceResult> {
  const res = await apiFetch<Envelope<DeleteServiceResult>>(`/api/services/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return res.data;
}
